// Public-facing expense reimbursement claim form.
// No auth required. Submits to POST /api/reimburse/submit.
// Receipt files are read as base64 and embedded in the JSON body (max 4 MB each).
//
// Bot check (bead cca-r11uc): when the build carries VITE_TURNSTILE_SITE_KEY the
// page draws Cloudflare's Turnstile widget and sends its token with the claim;
// the door verifies it. A missing token never holds the submit back (cca-8zf3),
// and without a key no widget is drawn at all. The send logic lives in
// ./claim-submit, where it is tested.

import { useEffect, useRef, useState } from 'react';
import {
  PortalHeader,
  ImageCapture,
  StickyActionBar,
  fieldProps,
  type CapturedImage,
} from '@cca/ui';
import { Turnstile } from '../components/Turnstile';
import { claimFormError, claimRequestBody, postClaim, type Expense } from './claim-submit';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

function todayNZT(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' });
}

function emptyExpense(): Expense {
  return {
    date: '',
    activity: '',
    supplier: '',
    description: '',
    amount: '',
    gst: '',
    receiptBase64: '',
    receiptFileName: '',
    receiptMimeType: '',
  };
}

export function ClaimFormPage() {
  const [activities, setActivities] = useState<string[]>([]);
  const [claimant, setClaimant] = useState({
    fullName: '',
    email: '',
    phone: '',
    bankAccount: '',
    bankAccountName: '',
  });
  const [expenses, setExpenses] = useState<Expense[]>([emptyExpense()]);
  const [declaration, setDeclaration] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped with every message shown: the alert's `key`, so the SAME message
  // twice running remounts it and is announced again (role="alert" speaks on a
  // DOM change; pattern: forms-web Register.tsx `validationFailures`).
  const [errorsShown, setErrorsShown] = useState(0);
  // The bot-check token, '' when the widget has none, and the widget's `key`,
  // bumped to remake it (and so get a fresh token) once a token has been sent.
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);
  // Read at render, not module load, so a test can set it per case.
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

  // WCAG 2.2 SC 4.1.3 (Miranda S1 on cca-r11uc). A refusal used to appear in a
  // box at the TOP of a long form while focus stayed on the Submit button at
  // the bottom: a screen reader said nothing, and on a phone the box was off
  // screen. Now each message is announced and takes focus, which also scrolls
  // it into view; the success panel does the same when it replaces the form.
  // (Pattern: forms-web Register.tsx submitAlertRef / SuccessPanel.)
  const errorRef = useRef<HTMLDivElement>(null);
  const doneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error, errorsShown]);
  useEffect(() => {
    if (submitted) doneRef.current?.focus();
  }, [submitted]);

  /** Show a message in the alert, and have it announced and focused even when
   *  it repeats the one already there. */
  const showError = (message: string): void => {
    setError(message);
    setErrorsShown((n) => n + 1);
  };

  useEffect(() => {
    void fetch(`${API_BASE}/api/reimburse/activities`)
      .then((r) => r.json() as Promise<{ activities: string[] }>)
      .then((d) => setActivities(d.activities))
      .catch(() => {
        setActivities([
          'Travel — Mileage / Fuel',
          'Travel — Public Transport / Taxi',
          'Accommodation',
          'Meals & Hospitality',
          'Other',
        ]);
      });
  }, []);

  const updateExpense = (i: number, field: keyof Expense, value: string) => {
    setExpenses((prev) => {
      const next = [...prev];
      const current = next[i];
      if (!current) return next;
      next[i] = { ...current, [field]: value };
      return next;
    });
  };

  // ImageCapture has already compressed the photo to a small JPEG data URL (or
  // passed a PDF through) — we just store its base64 payload on the expense.
  const setReceipt = (i: number, img: CapturedImage | null): void => {
    setExpenses((prev) => {
      const next = [...prev];
      const cur = next[i];
      if (!cur) return next;
      next[i] = img
        ? {
            ...cur,
            receiptBase64: img.dataUrl.split(',')[1] ?? '',
            receiptFileName: img.fileName,
            receiptMimeType: img.mimeType,
          }
        : { ...cur, receiptBase64: '', receiptFileName: '', receiptMimeType: '' };
      return next;
    });
  };

  const addExpense = () => setExpenses((prev) => [...prev, emptyExpense()]);
  const removeExpense = (i: number) =>
    setExpenses((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const handleSubmit = async () => {
    setError(null);
    // The bot-check token is not consulted here: a missing one never holds
    // the claim back (cca-8zf3). The door decides.
    const invalid = claimFormError(claimant, expenses, declaration, todayNZT());
    if (invalid) {
      showError(invalid);
      return;
    }

    setSubmitting(true);
    try {
      const body = claimRequestBody(claimant, expenses, crypto.randomUUID(), turnstileToken);
      // A token is single-use at Cloudflare, so this one is spent the moment it
      // is sent, whatever the answer. Forget it and remake the widget now, so a
      // retry carries a fresh token (or none), never this one (cca-4q19 on the
      // forms twin).
      setTurnstileToken('');
      setTurnstileKey((k) => k + 1);
      const result = await postClaim(API_BASE, body);
      if (!result.ok) {
        showError(result.message);
        return;
      }
      setSubmitted(result.submissionId);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-dvh" style={{ background: 'var(--paper)' }}>
        <PortalHeader portalName="Finance Portal" toolName="Expense Reimbursement Claim" />
        <div className="page-narrow" style={{ paddingTop: 40, paddingBottom: 40 }}>
          {/* Replaces the whole form, so it is announced and takes focus. */}
          <div
            ref={doneRef}
            tabIndex={-1}
            role="status"
            aria-live="polite"
            className="panel"
            style={{ borderLeft: '4px solid var(--green)' }}
          >
            <h2 style={{ color: 'var(--green)', margin: '0 0 8px', fontSize: 18 }}>
              Claim Submitted
            </h2>
            <p style={{ color: 'var(--text)', margin: '0 0 8px', fontSize: 14 }}>
              Your submission has been received and the registrar has been told. You'll usually get
              a confirmation email shortly.
            </p>
            <p
              style={{
                fontFamily: 'monospace',
                fontWeight: 600,
                color: 'var(--green)',
                margin: 0,
                fontSize: 13,
              }}
            >
              Submission ID: {submitted}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-dvh" style={{ background: 'var(--paper)' }}>
      <PortalHeader portalName="Finance Portal" toolName="Expense Reimbursement Claim" />

      <div className="page-narrow" style={{ paddingTop: 32, paddingBottom: 40 }}>
        {/* role="alert" announces it; the ref + tabIndex take focus to it (the
            effect above); `key` re-announces a repeat. overflow-wrap lets the
            registrar's address break on a 320px screen (Miranda S6). */}
        {error && (
          <div
            key={errorsShown}
            ref={errorRef}
            tabIndex={-1}
            role="alert"
            className="pill-danger"
            style={{
              borderRadius: 6,
              padding: '10px 16px',
              marginBottom: 16,
              fontSize: 14,
              background: 'var(--red-lt)',
              color: 'var(--red)',
              border: '1px solid var(--red-lt)',
              overflowWrap: 'anywhere',
            }}
          >
            {error}
          </div>
        )}

        {/* Claimant Details */}
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-section">
            <div className="panel-section-title">Your Details</div>
            <div className="grid-2">
              <div className="grid-span-all">
                <label className="input-label" htmlFor="claim-full-name">
                  Full Name *
                </label>
                <input
                  id="claim-full-name"
                  className="input"
                  {...fieldProps('name')}
                  maxLength={200}
                  value={claimant.fullName}
                  onChange={(e) => setClaimant((p) => ({ ...p, fullName: e.target.value }))}
                  placeholder="Your full legal name"
                />
              </div>
              <div>
                <label className="input-label" htmlFor="claim-email">
                  Email Address *
                </label>
                <input
                  id="claim-email"
                  className="input"
                  {...fieldProps('email')}
                  value={claimant.email}
                  onChange={(e) => setClaimant((p) => ({ ...p, email: e.target.value }))}
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="input-label" htmlFor="claim-phone">
                  Phone Number
                </label>
                <input
                  id="claim-phone"
                  className="input"
                  {...fieldProps('phone')}
                  maxLength={50}
                  value={claimant.phone}
                  onChange={(e) => setClaimant((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="input-label" htmlFor="claim-bank-account">
                  Bank Account Number
                </label>
                <input
                  id="claim-bank-account"
                  className="input"
                  type="text"
                  maxLength={200}
                  value={claimant.bankAccount}
                  onChange={(e) => setClaimant((p) => ({ ...p, bankAccount: e.target.value }))}
                  placeholder="e.g. 12-3456-7890123-00"
                />
              </div>
              <div>
                <label className="input-label" htmlFor="claim-bank-account-name">
                  Bank Account Name
                </label>
                <input
                  id="claim-bank-account-name"
                  className="input"
                  type="text"
                  maxLength={200}
                  value={claimant.bankAccountName}
                  onChange={(e) => setClaimant((p) => ({ ...p, bankAccountName: e.target.value }))}
                  placeholder="Name on bank account"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Expenses */}
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-section">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <div className="panel-section-title" style={{ marginBottom: 0 }}>
                Expenses
              </div>
              {expenses.length < 20 && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={addExpense}>
                  + Add expense
                </button>
              )}
            </div>

            {expenses.map((e, i) => (
              <div
                key={i}
                style={{
                  border: '1px solid var(--border-soft)',
                  borderRadius: 8,
                  padding: 14,
                  marginBottom: 12,
                  background: 'var(--paper)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <strong style={{ color: 'var(--brand)', fontSize: 13 }}>Expense {i + 1}</strong>
                  {expenses.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => removeExpense(i)}
                      style={{ fontSize: 11, color: 'var(--text2)' }}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid-2">
                  <div>
                    <label className="input-label" htmlFor={`claim-expense-date-${i}`}>
                      Date of Expense
                    </label>
                    <input
                      id={`claim-expense-date-${i}`}
                      className="input"
                      type="date"
                      value={e.date}
                      max={todayNZT()}
                      onChange={(ev) => updateExpense(i, 'date', ev.target.value)}
                    />
                  </div>
                  <div>
                    <label className="input-label" htmlFor={`claim-expense-category-${i}`}>
                      Category
                    </label>
                    <select
                      id={`claim-expense-category-${i}`}
                      className="input"
                      value={e.activity}
                      onChange={(ev) => updateExpense(i, 'activity', ev.target.value)}
                    >
                      <option value="">Select…</option>
                      {activities.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="input-label" htmlFor={`claim-expense-supplier-${i}`}>
                      Supplier / Vendor
                    </label>
                    <input
                      id={`claim-expense-supplier-${i}`}
                      className="input"
                      type="text"
                      maxLength={200}
                      value={e.supplier}
                      onChange={(ev) => updateExpense(i, 'supplier', ev.target.value)}
                      placeholder="Who was paid?"
                    />
                  </div>
                  <div>
                    <label className="input-label" htmlFor={`claim-expense-amount-${i}`}>
                      Amount (NZD) *
                    </label>
                    <input
                      id={`claim-expense-amount-${i}`}
                      className="input"
                      {...fieldProps('amount')}
                      value={e.amount}
                      onChange={(ev) => updateExpense(i, 'amount', ev.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="grid-span-all">
                    <label className="input-label" htmlFor={`claim-expense-description-${i}`}>
                      Description
                    </label>
                    <input
                      id={`claim-expense-description-${i}`}
                      className="input"
                      type="text"
                      maxLength={2000}
                      value={e.description}
                      onChange={(ev) => updateExpense(i, 'description', ev.target.value)}
                      placeholder="Brief description of the expense"
                    />
                  </div>
                  <div>
                    <span className="input-label">GST / Tax Included?</span>
                    <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                      {(['yes', 'no'] as const).map((opt) => (
                        <label
                          key={opt}
                          style={{
                            fontSize: 14,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <input
                            type="radio"
                            name={`gst-${i}`}
                            value={opt}
                            checked={e.gst === opt}
                            onChange={() => updateExpense(i, 'gst', opt)}
                          />
                          {opt === 'yes' ? 'Yes' : 'No'}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid-span-all">
                    <span className="input-label">Receipt (optional)</span>
                    <ImageCapture
                      label="Receipt"
                      value={
                        e.receiptFileName
                          ? {
                              dataUrl: `data:${e.receiptMimeType};base64,${e.receiptBase64}`,
                              fileName: e.receiptFileName,
                              mimeType: e.receiptMimeType,
                            }
                          : null
                      }
                      onChange={(img) => setReceipt(i, img)}
                      onRemove={() => setReceipt(i, null)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Declaration */}
        <div className="panel" style={{ marginBottom: 20 }}>
          <div className="panel-section">
            <div className="panel-section-title">Declaration</div>
            <label
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              <input
                type="checkbox"
                checked={declaration}
                onChange={(e) => setDeclaration(e.target.checked)}
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <span style={{ color: 'var(--text)' }}>
                I declare that the expenses listed above were genuinely incurred in the service of
                the Church of Confessing Anglicans Aotearoa New Zealand, that receipts are accurate,
                and that I have not previously claimed reimbursement for these items.
              </span>
            </label>
            {/* Renders nothing without a site key; usually invisible with one. */}
            <Turnstile key={turnstileKey} siteKey={siteKey} onVerify={setTurnstileToken} />
          </div>
        </div>

        {/* Full width as the shared .btn-block, which in the bar fills what the
            docked "Report a problem" launcher leaves, so on a phone the two
            share one row (cca-00fff); an inline 100% width pushed the launcher
            onto a row of its own. */}
        <StickyActionBar>
          <button
            type="button"
            className={`btn btn-block ${declaration && !submitting ? 'btn-primary' : 'btn-ghost'}`}
            style={{
              padding: '13px 0',
              fontSize: 15,
              fontWeight: 600,
              justifyContent: 'center',
            }}
            onClick={() => void handleSubmit()}
            disabled={submitting || !declaration}
          >
            {submitting ? 'Submitting…' : 'Submit Claim'}
          </button>
        </StickyActionBar>
      </div>
    </div>
  );
}
